<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { QueryParams } from '@client/utils/query-params';

  /**
   * Total number of records in the dataset.
   * @type {object}
   * @default
   */
  export let totalRecords;

  /**
   * Current page number being displayed.
   * @type {object}
   * @default 1
   */
  export let currentPage;

  /**
   * Number of records to display per page.
   * @type {object}
   * @default 10
   */
  export let perPage;

  /**
   * Callback function triggered when the page changes.
   * @type {Function}
   * @default () => {}
   */
  export let onChangePage = () => {};

  /**
   * Callback function triggered when the number of records per page changes.
   * @type {Function}
   * @default () => {}
   */
  export let onChangeRecords = () => {};

  let pages = [];

  const onPageChange = (page) => {
    const lastPageNumber = Math.ceil(totalRecords / perPage);

    if (lastPageNumber === 0) return;

    if (page < 1) {
      currentPage = lastPageNumber;
    } else if (page > lastPageNumber) {
      currentPage = 1;
    } else {
      currentPage = page;
    }

    QueryParams.set('page', currentPage);
    onChangePage(currentPage);
  };

  const onPerPageChange = (total) => {
    if (perPage !== total) {
      perPage = total;
      onChangeRecords(perPage);
    }

    if (currentPage !== 1) {
      currentPage = 1;
    }

    QueryParams.set('page', currentPage);
    QueryParams.set('perpage', perPage);
  };

  const getPageNumbers = (current, perPage, totalRecords, pageNumbersToShow = 3) => {
    const lastPageNumber = Math.ceil(totalRecords / perPage);
    const currentPageNumber = current <= lastPageNumber ? current : lastPageNumber;
    const maxPagesBeforeCurrentPage = Math.floor(pageNumbersToShow / 2);
    const maxPagesAfterCurrentPage = Math.ceil(pageNumbersToShow / 2) - 1;
    let startPage = 1;
    let endPage = lastPageNumber;

    if (lastPageNumber <= 1) {
      return [];
    }

    if (currentPageNumber <= maxPagesBeforeCurrentPage) {
      // near the start
      startPage = 1;
      endPage = pageNumbersToShow;
    } else if (currentPageNumber + maxPagesAfterCurrentPage >= lastPageNumber) {
      startPage = Math.max(1, lastPageNumber - pageNumbersToShow + 1);
    } else {
      // somewhere in the middle
      startPage = currentPageNumber - maxPagesBeforeCurrentPage;
      endPage = currentPageNumber + maxPagesAfterCurrentPage;
    }

    let pageNumbers = Array.from(Array(endPage + 1 - startPage).keys())
      .map((pageNumber) => startPage + pageNumber)
      .filter((pageNumber) => pageNumber <= lastPageNumber && pageNumber > 0);

    if (pageNumbers[0] > 1) {
      if (pageNumbers[0] <= 2) {
        pageNumbers = [1, ...pageNumbers];
      } else {
        const ellipsis = pageNumbers[0] > 3 ? '...' : 2;
        pageNumbers = [1, ellipsis, ...pageNumbers];
      }
    }

    if (pageNumbers[pageNumbers.length - 1] !== lastPageNumber) {
      if (pageNumbers[pageNumbers.length - 1] === lastPageNumber - 1) {
        pageNumbers = [...pageNumbers, lastPageNumber];
      } else {
        const ellipsis = pageNumbers[pageNumbers.length - 1] < lastPageNumber - 2 ? '...' : lastPageNumber - 1;
        pageNumbers = [...pageNumbers, ellipsis, lastPageNumber];
      }
    }

    return pageNumbers;
  };

  const setQueryParams = () => {
    const page = QueryParams.get('page');
    const perpage = QueryParams.get('perpage');
    const lastPageNumber = Math.ceil(totalRecords / perPage);

    if (perpage) {
      if (perPage !== perpage) {
        perPage = perpage;
      }
    } else {
      QueryParams.set('perpage', perPage);
    }

    if (page) {
      if (page <= lastPageNumber) {
        if (currentPage !== page) {
          currentPage = page;
          QueryParams.set('page', currentPage);
        }
      } else {
        currentPage = lastPageNumber;
        QueryParams.set('page', currentPage);
      }
    } else {
      QueryParams.set('page', 1);
    }
  };

  const updateQueryParams = () => {
    const lastPageNumber = Math.ceil(totalRecords / perPage);

    QueryParams.set('perpage', perPage);

    if (currentPage <= lastPageNumber) {
      QueryParams.set('page', currentPage);
    } else {
      currentPage = lastPageNumber;
      QueryParams.set('page', lastPageNumber);
    }
  };

  onMount(setQueryParams);

  $: pages = getPageNumbers(currentPage, perPage, totalRecords);
  $: recordsStart = Math.min(totalRecords, (currentPage === 0 ? 0 : currentPage - 1) * perPage + 1);
  $: recordsEnd = Math.min(totalRecords, (currentPage === 0 ? 0 : currentPage - 1) * perPage + perPage);
  $: totalRecords < perPage * currentPage && currentPage > 0 && onPageChange(currentPage);
  $: if (currentPage !== undefined && totalRecords) {
    updateQueryParams();
  }
</script>

<nav class="pagination">
  {#if pages.length > 1}
    <ul>
      <li class="pagination-prev">
        <button type="button" on:click={() => onPageChange(currentPage - 1)}>
          <Icon name="chevron-left" />
        </button>
      </li>

      {#each pages as page, i}
        {#if page === '...'}
          <li class="pagination-page">
            <Icon name="more-horizontal" style="margin-right: var(--margin-base)" />
          </li>
        {:else}
          <li class="pagination-page" class:active={page === currentPage}>
            <button type="button" on:click={() => onPageChange(page)}>
              {page.toLocaleString()}
            </button>
          </li>
        {/if}
      {/each}

      <li class="pagination-next">
        <button type="button" on:click={() => onPageChange(currentPage + 1)}>
          <Icon name="chevron-right" />
        </button>
      </li>
    </ul>
  {:else}
    <span />
  {/if}

  <div class="pagination-records">
    <span class="count" data-testid="pagination-count">
      {$format('label.SHOWING_X_OF_X', {
        values: {
          start: recordsStart,
          end: Number(recordsEnd).toLocaleString(),
          total: Number(totalRecords).toLocaleString()
        }
      })}
    </span>
    <Dropdown>
      <DropdownToggle tag="div">
        {@html $format('label.PER_PAGE_X', { values: { value: perPage } })}
        <Icon name="chevron-down" />
      </DropdownToggle>
      <DropdownMenu>
        {#each [5, 10, 25, 50, 100, 250, 500] as records}
          <DropdownItem active={perPage === records} on:click={() => onPerPageChange(records)}>
            {records}
          </DropdownItem>
        {/each}
      </DropdownMenu>
    </Dropdown>
  </div>
</nav>

<style lang="scss" src="./pagination.scss"></style>
